<template>
  <label
    :class="[
      'ui-checkbox',
      sizeClass,
      { 'ui-checkbox--disabled': disabled },
    ]"
  >
    <input
      :id="id"
      ref="inputRef"
      type="checkbox"
      class="ui-checkbox__input"
      :name="name"
      :value="value"
      :checked="isChecked"
      :disabled="disabled"
      :aria-label="ariaLabel"
      :aria-describedby="ariaDescribedby"
      :aria-controls="ariaControls"
      @change="onChange"
    >
    <span class="ui-checkbox__label" v-if="$slots.default">
      <slot />
    </span>
  </label>
</template>

<script setup lang="ts">
  /**
   * UiCheckbox — Tailwind primitive replacing BootstrapVue-Next's
   * `b-form-checkbox`.
   *
   * Two modes (matching BVN behaviour so swap-in is mechanical):
   *
   * 1. Boolean (single toggle):
   *      <UiCheckbox v-model="isActive">Is Active</UiCheckbox>
   *
   *    Optional `unchecked-value` mirrors BVN — when bound to a non-bool
   *    field the prop pair `value` / `unchecked-value` decides what gets
   *    emitted on toggle (e.g. 'Y' / 'N').
   *
   * 2. Array (multi-select inside a list):
   *      <UiCheckbox v-model="selectedIds" :value="row.id" />
   *
   *    When `modelValue` is an array, toggling pushes/removes `value` —
   *    matching `b-form-checkbox` inside a table row.
   *
   * Indeterminate state is supported imperatively (BVN parity for the
   * "Select all" pattern); changes via the `indeterminate` prop drive a
   * watcher that sets the underlying input's property since there's no
   * matching HTML attribute.
   */
  import { computed, watch, onMounted, useTemplateRef } from 'vue'

  defineOptions({ name: 'UiCheckbox' })

  type Primitive = string | number | boolean | null
  type ModelValue = boolean | Primitive | Primitive[] | null

  interface Props {
    modelValue?: ModelValue
    /** Per-item value when modelValue is an array (or non-bool single). */
    value?: Primitive
    /** Emitted when unchecked in non-array mode (BVN parity). */
    uncheckedValue?: Primitive
    indeterminate?: boolean
    disabled?: boolean
    size?: 'sm' | 'md' | 'lg'
    id?: string
    name?: string
    ariaLabel?: string
    ariaDescribedby?: string
    ariaControls?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    value: true,
    uncheckedValue: false,
    indeterminate: false,
    disabled: false,
    size: 'md',
    id: undefined,
    name: undefined,
    ariaLabel: undefined,
    ariaDescribedby: undefined,
    ariaControls: undefined,
  })

  const emit = defineEmits<{
    'update:modelValue': [value: ModelValue]
    'change': [value: ModelValue]
  }>()

  const inputRef = useTemplateRef<HTMLInputElement>('inputRef')

  const sizeClass = computed(() => `ui-checkbox--${props.size}`)

  // Two modes folded into a single check:
  // - array v-model: checked iff `value` is in the array
  // - boolean / scalar v-model: checked iff equal to `value` (prop default
  //   `true` keeps the bare-bones `v-model="bool"` case ergonomic)
  const isChecked = computed(() => {
    if (Array.isArray(props.modelValue)) {
      return props.modelValue.includes(props.value)
    }
    return props.modelValue === props.value
  })

  function onChange (event: Event) {
    const checked = (event.target as HTMLInputElement).checked
    let next: ModelValue
    if (Array.isArray(props.modelValue)) {
      // Always emit a fresh array so v-model reactivity fires reliably
      // even when the parent stores it by reference.
      next = checked
        ? [...props.modelValue, props.value]
        : props.modelValue.filter(v => v !== props.value)
    } else {
      next = checked ? props.value : props.uncheckedValue
    }
    emit('update:modelValue', next)
    emit('change', next)
  }

  // `indeterminate` has no HTML attribute — must be set as a property on
  // the live element. Sync on mount and whenever the prop flips.
  function syncIndeterminate () {
    if (inputRef.value) inputRef.value.indeterminate = props.indeterminate
  }
  onMounted(syncIndeterminate)
  watch(() => props.indeterminate, syncIndeterminate)

  defineExpose({
    focus: () => inputRef.value?.focus(),
    blur: () => inputRef.value?.blur(),
    input: inputRef,
  })
</script>

<style scoped>
  .ui-checkbox {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    user-select: none;
    color: #111827;                     /* gray-900 */
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
  .ui-checkbox--disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  /* Size-tunable check via accent-color so we get the platform's native
     focus ring + checkmark glyph for free, while keeping the brand
     primary-600 fill consistent with UiInput's focus colour. */
  .ui-checkbox__input {
    flex: 0 0 auto;
    margin: 0;
    accent-color: #2563eb;              /* primary-600 */
    cursor: inherit;
    /* Nudge alignment so descender of the label sits on the box centre. */
    transform: translateY(0.5px);
  }
  .ui-checkbox__input:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
    border-radius: 2px;
  }
  .ui-checkbox__input:disabled {
    cursor: not-allowed;
  }

  .ui-checkbox--sm .ui-checkbox__input { width: 0.875rem; height: 0.875rem; }
  .ui-checkbox--md .ui-checkbox__input { width: 1rem;     height: 1rem; }
  .ui-checkbox--lg .ui-checkbox__input { width: 1.125rem; height: 1.125rem; }

  .ui-checkbox--sm { font-size: 0.8125rem; }
  .ui-checkbox--lg { font-size: 1rem; }
</style>
